import type { DirectiveBinding } from "vue"
import { httpUrl } from "~/utils/httpUrl"

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive("append-parameters", {
    beforeMount: appendParameters,
    updated: appendParameters,
  })
})

/**
 * Forwards the request's tracking parameters onto every external link in the
 * subtree.
 *
 * The bag overwrites a parameter the link already carries, and that is
 * deliberate: apply URLs ship with placeholder defaults meant to be replaced —
 * a PitchYou link arrives as `?b=37&oid=&quelle=0&…`, and the visitor's real
 * `quelle` has to win over the `0`.
 *
 * The one value that must not be overwritten is the `utm_source` resolved from
 * the JobShop's tracking settings (JET-2719). Rather than teach this directive
 * about it, the caller hands the subtree holding the PitchYou link a bag that
 * already carries the resolved value — see `heroUrlParams` in
 * components/ContentModules.vue.
 */
export function appendParameters(el: HTMLElement, binding: DirectiveBinding<QueryHeader[]>) {
  // Get all <a> tags & filter them
  const links = Array.from(el.querySelectorAll("a")).filter((linkEl) => {
    const href = linkEl.getAttribute("href")
    // Exclude <a> tags without href and with already appended parameters
    if (!href || includesQueryHeaders(binding.value, href))
      return false

    // Only external http(s) links — internal paths and fragments, mailto:, tel:,
    // sms: and anything unparseable are left alone.
    return !!httpUrl(href)
  })

  // Append parameters to href of filtered <a> tags
  links.forEach((link) => {
    // Non-null: the filter above already parsed this href successfully.
    const href = httpUrl(link.getAttribute("href"))!
    const params = new URLSearchParams(href.search)

    binding.value.forEach((queryHeader) => {
      params.set(queryHeader.key, queryHeader.value)
    })

    href.search = params.toString()
    link.setAttribute("href", href.toString())
  })
}

function includesQueryHeaders(queryHeaders: QueryHeader[], url: string): boolean {
  const params = new URLSearchParams()

  queryHeaders.forEach((queryHeader) => {
    params.set(queryHeader.key, queryHeader.value)
  })

  return url.includes(params.toString())
}
